// /ea/{id}: an exam assigned to a class (RFC-048 "The Exams page",
// byj-1wv.56.5). Its instructor reads every hand-in, student × question, with
// its grade — the checks, the measures against the budgets, the agent's use,
// Prism's items, the recording — overrides a score with a note, releases it,
// and exports the lot. A student reads the exam, starts each question on
// their machine with the command to copy, follows where each stands, and
// reads the grades released, without the checks' output. On a relay that
// hosts machines, a student granted them may sit it on a Share machine of
// the exam's own (byj-1wv.62.10). Laid out as the class pages are
// (byj-1wv.51.12): the class a link in the head's line, who is sitting as
// rows, the hand-ins a table that scrolls by itself on a phone; a student's
// page says how to sit it, step by step, and leads a grade with its score.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type ExamAssignmentPage, type ExamDispute } from "../api";
import { CommandLine } from "../components/CommandLine";
import { ExamGuideButton } from "../components/ExamGuide";
import { Steps } from "../components/Steps";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLoading, PageNotice } from "../components/Page";
import { FoldPanel } from "../components/SidePanel";
import { useNow } from "../now";
import { barWords, questionState, scoreOf, windowState, type WindowState } from "../exams";
import { t, whenMinute, word, phrases } from "../i18n";
import { Rubric } from "../components/Rubric";
import { GradeReasons, Disputes, DisputeDialog, Score, SittingFacts, InstructorView, AssignmentBar, ExamMachine } from "../components/ExamStaff";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/**
 * A question of a student's: one line — its id, mode, state and, released,
 * its score — opening on what it asks, its bar, the command that starts it,
 * and its grade led by its score, then why: the note, the reasons, the
 * disputes. Open while it is still to do; folded once graded, its score on
 * its line (byj-1wv.51.12). Outside the window the relay starts nothing, so
 * no command to start it: before, when it opens; after, that it is closed —
 * or, begun with hand-ins left, how to hand it in late.
 */
/** A :choice question's options, picked and handed in from the page (RFC-055). */
function ChoiceAnswer({ q, send }: { q: ExamAssignmentPage["meta"]["questions"][number]; send: (answers: number[]) => Promise<void> }) {
  const choose = q.choose ?? 1;
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = (n: number) => setPicked((xs) => (choose === 1 ? [n] : xs.includes(n) ? xs.filter((x) => x !== n) : [...xs, n]));
  return (
    <form
      className="stack exam-choice"
      data-testid="choice-answer"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        send([...picked].sort((a, b) => a - b)).then(
          () => setBusy(false),
          (err: unknown) => {
            setError(errText(err));
            setBusy(false);
          },
        );
      }}
    >
      <fieldset className="stack">
        <legend className="small muted">{t("exams:choicePick", { count: choose })}</legend>
        {(q.options ?? []).map((o, i) => (
          <label key={i} className="toggle">
            <input type={choose === 1 ? "radio" : "checkbox"} name={`choice-${q.id}`} checked={picked.includes(i + 1)} onChange={() => toggle(i + 1)} />
            {i + 1}. {o}
          </label>
        ))}
      </fieldset>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      <div className="row">
        <button type="submit" className="primary" disabled={busy || picked.length !== choose}>
          {t("exams:choiceHandIn")}
        </button>
      </div>
    </form>
  );
}

function StudentQuestion({ q, at, cmd, examWindow, opensAt, handIns, added, disputing, onDispute, onAnswer }: {
  q: ExamAssignmentPage["meta"]["questions"][number];
  at: ExamAssignmentPage["attempts"][number] | undefined;
  /** the command that starts it with byj; "" for one who sits it on a Share machine alone */
  cmd: string;
  examWindow: WindowState;
  opensAt?: string;
  /** the hand-ins the exam allows a question */
  handIns: number;
  added: ExamDispute[];
  /** its grade's dispute is open, its Dispute naming the dialog */
  disputing: boolean;
  /** asks the student's view to open the dispute of this released grade */
  onDispute: (gradeId: string, submission: string) => void;
  /** a :choice question's answers handed in from the page (RFC-055) */
  onAnswer?: (answers: number[]) => Promise<void>;
}) {
  const state = questionState(at);
  const released = at?.submissions.filter((s) => s.grade?.released).pop();
  const [open] = useState(state !== "released");
  const ds = released ? [...(released.disputes ?? []), ...added] : [];
  const handed = at?.submissions.filter((s) => s.uploaded).length ?? 0;
  const choice = q.mode === "choice";
  const next =
    state === "released" ? null : choice ? (
      handed < handIns && (examWindow === "open" || at) && onAnswer ? <ChoiceAnswer q={q} send={onAnswer} /> : handed > 0 ? <p className="muted small flush">{t("exams:choiceHandedIn")}</p> : null
    ) : !cmd ? null : examWindow === "open" ? (
      <div className="stack exam-start">
        <span className="small muted">{t("exams:examStartCommand")}</span>
        <CommandLine command={cmd} />
      </div>
    ) : examWindow === "not-yet" ? (
      <p className="muted small flush" data-testid="question-window">
        {t("exams:questionOpensAt", { w: opensAt ? whenMinute(opensAt) : "" })}
      </p>
    ) : !at ? (
      <p className="muted small flush" data-testid="question-window">
        {t("exams:questionClosed")}
      </p>
    ) : handed < handIns ? (
      <div className="stack exam-start" data-testid="question-window">
        <span className="small muted">{t("exams:questionLateHandIn")}</span>
        <CommandLine command="byj exam submit" />
      </div>
    ) : null;
  return (
    <li className="card exam-question">
      <details open={open || undefined}>
        <summary className="row wrap">
          <code className="grow">{q.id}</code>
          <span className="chip">{word("exams:questionMode", q.mode) ?? q.mode}</span>
          {state === "sitting" && at?.share_live && <span className="chip chip-live">{t("exams:sharedWithInstructor")}</span>}
          <span className={`chip ${state === "released" ? "chip-live" : state === "not-started" ? "" : "chip-warn"}`} data-testid="question-state">
            {word("exams:questionState", state)}
          </span>
          {released?.grade && <strong className="question-score">{scoreOf(released.grade).toFixed(1)}</strong>}
        </summary>
        <div className="stack exam-question-body">
          <p className="pre-wrap flush">{q.prompt}</p>
          <p className="muted small flush">
            {q.submit && q.submit.length > 0 && phrases.submitWhat(q.submit)}
            {q.submit && q.submit.length > 0 && q.criteria && " · "}
            {q.criteria && barWords(q.criteria)}
          </p>
          {next}
          {released?.grade && (
            <div className="stack released-grade" data-testid="released-grade">
              <div className="row wrap grade-score">
                <span className="muted small">{t("exams:yourGrade")}</span>
                <Score g={released.grade} />
                <span className="muted small">{t("exams:scoreOutOf")}</span>
                <span className="grow" />
                {!ds.some((d) => !d.answered_at) && (
                  <button
                    className="small"
                    aria-haspopup="dialog"
                    aria-expanded={disputing}
                    aria-controls={disputing ? "dispute-dialog" : undefined}
                    onClick={() => onDispute(released.grade!.id, released.id)}
                  >
                    {t("exams:disputeGrade")}
                  </button>
                )}
              </div>
              {released.grade.override_note && <p className="small flush">{released.grade.override_note}</p>}
              <GradeReasons detail={released.grade.detail} />
              <Disputes disputes={ds} />
            </div>
          )}
        </div>
      </details>
    </li>
  );
}

/**
 * A student's view (byj-1wv.51.12): the bar says how the assignment stands
 * and how far they have got; how to sit it and the rules and rubric folded
 * under it — the steps open until a question is started; a Share machine on
 * a line; then the questions.
 */
function StudentView({ page, now, dispute, machine, answerChoice, onChanged, cli }: {
  page: ExamAssignmentPage;
  now: number;
  /** Shown how to sit it with byj; a third party's candidate sits it on a Share machine alone (byj-1wv.117.10). */
  cli: boolean;
  dispute: (gradeId: string, text: string) => Promise<ExamDispute>;
  machine: { load: typeof api.machines; start: typeof api.startMachine };
  answerChoice: typeof api.answerChoice;
  onChanged: () => void;
}) {
  const [added, setAdded] = useState<Record<string, ExamDispute[]>>({});
  // the released grade whose dispute is open, its dialog drawn here, over the page
  const [disputing, setDisputing] = useState<{ gradeId: string; submission: string } | null>(null);
  const { meta, assignment: a } = page;
  const byQ = useMemo(() => new Map(page.attempts.map((x) => [x.question, x])), [page.attempts]);
  // a :choice question answered on the page holds the attempt to no kind (RFC-055)
  const kind = page.attempts.find((x) => x.agent !== "web")?.agent ?? (meta.agents.length === 1 ? meta.agents[0]! : "KIND");
  const graded = meta.questions.filter((q) => questionState(byQ.get(q.id)) === "released").length;
  // the relay starts nothing outside the window; after it, a hand-in is still taken, late
  const examWindow = windowState(a, now);
  const limits = [meta.agents.join(", "), meta.time_limit_min ? t("exams:examTimeLimit", { m: meta.time_limit_min }) : null, t("exams:examAttemptsAllowed", { count: meta.attempts ?? 1 })].filter(Boolean).join(" · ");
  return (
    <>
      <AssignmentBar page={page} now={now}>
        <span className="share-bar-facts">{t("exams:examProgress", { graded, total: meta.questions.length })}</span>
        {/* begun as one sitting (RFC-058): its deadline, on the relay's clock */}
        {page.sitting && (
          <span className="share-bar-facts" data-testid="my-sitting">
            <SittingFacts s={page.sitting} />
          </span>
        )}
      </AssignmentBar>
      <div className="exam-folds">
        {cli && examWindow !== "closed" && (
          <FoldPanel label={t("exams:examSitTitle")} summary={<span className="muted small">{t("exams:examSitSummary")}</span>} className="card class-policy-fold" open={page.attempts.length === 0} testid="exam-how">
            <Steps steps={phrases.examSitSteps(a.id, kind)} testid="exam-sit-steps" />
          </FoldPanel>
        )}
        <FoldPanel label={t("exams:examRules")} summary={<span className="muted small">{limits}</span>} className="card class-policy-fold" testid="exam-rules">
          <p className="muted small">
            {t("exams:examAgents")} {limits}
          </p>
          {meta.rubric && <Rubric rubric={meta.rubric} />}
        </FoldPanel>
      </div>
      <ExamMachine assignment={a.id} load={machine.load} start={machine.start} canStart={examWindow === "open"} />
      <section aria-labelledby="exam-my-questions" className="exam-section stack">
        <h2 id="exam-my-questions" className="rows-head flush">
          {t("exams:examQuestions")} · {meta.questions.length}
        </h2>
        <ul className="exam-question-list" data-testid="student-questions">
          {meta.questions.map((q) => (
            <StudentQuestion
              key={q.id}
              q={q}
              at={byQ.get(q.id)}
              cmd={cli ? `byj exam start ${a.id} ${q.id} --agent ${kind}` : ""}
              examWindow={examWindow}
              opensAt={a.opens_at}
              handIns={meta.attempts ?? 1}
              added={(() => {
                const released = byQ.get(q.id)?.submissions.filter((s) => s.grade?.released).pop();
                return released ? (added[released.id] ?? []) : [];
              })()}
              disputing={!!disputing && byQ.get(q.id)?.submissions.some((s) => s.id === disputing.submission) === true}
              onDispute={(gradeId, submission) => setDisputing({ gradeId, submission })}
              onAnswer={async (answers) => {
                await answerChoice(a.id, q.id, answers);
                onChanged();
              }}
            />
          ))}
        </ul>
      </section>
      {disputing && (
        <DisputeDialog
          send={async (text) => {
            const d = await dispute(disputing.gradeId, text);
            setAdded((m) => ({ ...m, [disputing.submission]: [...(m[disputing.submission] ?? []), d] }));
          }}
          onClose={() => setDisputing(null)}
        />
      )}
    </>
  );
}

export function ExamAssignment({ id, load = api.examAssignment, loadSubs = api.examSubmissions, patch = api.patchGrade, answer = api.answerDispute, dispute = api.dispute, machines = api.machines, startMachine = api.startMachine, answerChoice = api.answerChoice, cli = true }: {
  id: string;
  load?: typeof api.examAssignment;
  loadSubs?: typeof api.examSubmissions;
  patch?: typeof api.patchGrade;
  answer?: typeof api.answerDispute;
  dispute?: typeof api.dispute;
  machines?: typeof api.machines;
  startMachine?: typeof api.startMachine;
  answerChoice?: typeof api.answerChoice;
  /** The student is shown how to sit it with byj; not a third party's candidate (byj-1wv.117.10). */
  cli?: boolean;
}) {
  const [page, setPage] = useState<ExamAssignmentPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  // how far this browser's clock is from the relay's, which decides the window
  const [skew, setSkew] = useState(0);
  const fetch = useCallback(() => {
    load(id).then(
      (p) => {
        const at = Date.parse(p.now);
        setSkew(Number.isNaN(at) ? 0 : at - Date.now());
        setPage(p);
      },
      (err: unknown) => setError(errText(err)),
    );
  }, [id, load]);
  useEffect(fetch, [fetch]);
  // whether its window is open, said again while the page is open, on the relay's clock
  const now = useNow() + skew;
  if (error) {
    return (
      <PageNotice error={error}>
        <Link to="/exams">{t("exams:examsTitle")}</Link>
      </PageNotice>
    );
  }
  if (!page) return <PageLoading />;
  const a = page.assignment;
  const instructor = page.role === "instructor";
  const staff = instructor || page.role === "reviewer";
  return (
    <Page className="exams-page">
      <PageHead
        title={a.exam.title}
        long
        actions={
          <>
            {instructor && <ExamGuideButton code={a.class.join_code} />}
            <Link to={instructor ? `/e/${encodeURIComponent(a.exam.id)}` : "/exams"} className="button">
              {instructor ? t("exams:openExam") : t("exams:examsTitle")}
            </Link>
          </>
        }
      />
      {staff ? (
        <InstructorView page={page} now={now} load={loadSubs} patch={patch} answer={answer} onChanged={fetch} />
      ) : (
        <StudentView page={page} now={now} dispute={dispute} machine={{ load: machines, start: startMachine }} answerChoice={answerChoice} onChanged={fetch} cli={cli} />
      )}
    </Page>
  );
}
